import React, { useEffect, useRef, useState } from 'react';

// Тарифы по количеству устройств
const DEVICE_TARIFFS = {
    1: 149, 2: 199, 3: 249, 4: 299, 5: 349,
    6: 399, 7: 449, 8: 499, 9: 549, 10: 599
};

// Коэффициенты для разных периодов
const PERIOD_MULTIPLIERS = {
    1: 1,      // 1 месяц - без скидки
    3: 3,      // 3 месяца - без скидки
    6: 5.4,    // 6 месяцев - 10% скидка (6 * 0.9 = 5.4)
    12: 10.2   // 12 месяцев - 15% скидка (12 * 0.85 = 10.2)
};

// Функция для получения цены тарифа
const getTariffPrice = (devices, periodMonths) => {
    const basePrice = DEVICE_TARIFFS[devices] || DEVICE_TARIFFS[1];
    const multiplier = PERIOD_MULTIPLIERS[periodMonths] || 1;
    return Math.round(basePrice * multiplier);
};

// Функция для получения обычной цены (без скидки)
const getRegularPrice = (devices, periodMonths) => {
    const basePrice = DEVICE_TARIFFS[devices] || DEVICE_TARIFFS[1];
    return basePrice * periodMonths;
};

// Функция для получения процента скидки
const getDiscountPercent = (periodMonths) => {
    if (periodMonths === 6) return 10;
    if (periodMonths === 12) return 15;
    return 0;
};

// Периоды для отображения
const periods = [
    { months: 1 },
    { months: 3 },
    { months: 6 },
    { months: 12 }
];

export function RenewSheet({ isVisible, onClose, onConfirm, devices = 1 }) {
    const sheetRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(periods[0]);
    const [startY, setStartY] = useState(null);
    const [currentY, setCurrentY] = useState(null);

    useEffect(() => {
        if (isVisible) {
            setIsOpen(true);
            document.body.style.overflow = 'hidden';
            document.body.classList.add('sheet-open');
        } else {
            setIsOpen(false);
            document.body.style.overflow = '';
            document.body.classList.remove('sheet-open');
        }

        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('sheet-open');
        };
    }, [isVisible]);

    // Вычисляем цены для выбранного периода
    const selectedMonths = selectedPeriod.months;
    const totalPrice = getTariffPrice(devices, selectedMonths);
    const regularPrice = getRegularPrice(devices, selectedMonths);
    const discountPercent = getDiscountPercent(selectedMonths);
    const hasDiscount = discountPercent > 0;

    const handleTouchStart = (e) => {
        setStartY(e.touches[0].clientY);
    };

    const handleTouchMove = (e) => {
        if (startY === null || !sheetRef.current) return;

        const currentTouchY = e.touches[0].clientY;
        const diff = currentTouchY - startY;

        if (diff > 0) {
            e.preventDefault();
            setCurrentY(diff);
            sheetRef.current.style.transform = `translateY(${diff}px)`;
        }
    };

    const handleTouchEnd = () => {
        if (startY === null) return;

        const diff = currentY || 0;

        if (diff > 100) {
            handleClose();
        } else {
            if (sheetRef.current) {
                sheetRef.current.style.transform = '';
            }
        }

        setStartY(null);
        setCurrentY(null);
    };

    const handleMouseDown = (e) => {
        setStartY(e.clientY);

        const handleMouseMove = (e) => {
            if (startY === null || !sheetRef.current) return;

            const diff = e.clientY - startY;

            if (diff > 0) {
                setCurrentY(diff);
                sheetRef.current.style.transform = `translateY(${diff}px)`;
            }
        };

        const handleMouseUp = () => {
            const diff = currentY || 0;

            if (diff > 100) {
                handleClose();
            } else {
                if (sheetRef.current) {
                    sheetRef.current.style.transform = '';
                }
            }

            setStartY(null);
            setCurrentY(null);

            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(() => {
            onClose();
        }, 300);
    };

    const handleConfirm = () => {
        onConfirm(selectedPeriod.months);
        handleClose();
    };
    const platform = window.Telegram?.WebApp?.platform;
    const isWindows = platform === 'windows' || platform === 'tdesktop' || platform === 'web';
    if (!isVisible) return null;

    return (
        <div className="renew-sheet-wrapper">
            <div className={`renew-sheet-overlay ${isOpen ? 'visible' : ''}`} onClick={handleClose} />
            <div
                ref={sheetRef}
                className={`renew-sheet ${isOpen ? 'open' : ''}`}
                style={{ padding: '12px 16px 16px 16px' }}
            >
                <div className="renew-sheet-content" style={(isWindows ? {gap: '12px', padding: '20px 16px 24px'} : {gap: '12px', padding: '0 16px 0'})}>
                    <div
                        className="renew-sheet-drag-handle"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={handleMouseDown}
                    >
                        <div className="drag-indicator" style={{ width: '32px', height: '3px' } } />
                    </div>

                    <div className="renew-sheet-header" style={{ marginBottom: '0' }}>
                        <h2 style={{ fontSize: '18px', margin: '0' }}>Продлить подписку</h2>
                        <p style={{ fontSize: '14px', marginTop: '4px' }}>Выберите срок продления</p>
                    </div>

                    <div className="renew-periods" style={{ gap: '8px' }}>
                        {periods.map(period => {
                            const months = period.months;
                            const periodPrice = getTariffPrice(devices, months);
                            const periodRegularPrice = getRegularPrice(devices, months);
                            const periodDiscount = getDiscountPercent(months);
                            const periodHasDiscount = periodDiscount > 0;

                            return (
                                <div
                                    key={months}
                                    className={`renew-period-item ${selectedPeriod.months === months ? 'selected' : ''}`}
                                    onClick={() => setSelectedPeriod(period)}
                                    style={{ padding: '12px' }}
                                >
                                    <div className="period-left">
                                        <span className="period-months" style={{ fontSize: '14px' }}>
                                            {months} {months === 1 ? 'месяц' : months === 3 ? 'месяца' : 'месяцев'}
                                        </span>
                                        {periodHasDiscount && (
                                            <span className="period-discount" style={{ fontSize: '11px' }}>-{periodDiscount}%</span>
                                        )}
                                    </div>
                                    <div className="period-right">
                                        <span className="period-price" style={{ fontSize: '16px', fontWeight: '600' }}>
                                            {periodPrice} ₽
                                        </span>
                                        {periodHasDiscount && (
                                            <span className="period-old-price" style={{ fontSize: '11px' }}>
                                                {periodRegularPrice} ₽
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="renew-sheet-total" style={{
                        background: 'linear-gradient(40deg, rgba(64, 82, 73, 0.3) 50%, #2a5a3a 100%)',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        border: 'none'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span style={{ fontSize: '14px' }}>Итого:</span>
                            <div style={{ textAlign: 'right' }}>
                                {hasDiscount && (
                                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through', marginRight: '8px' }}>
                                        {regularPrice} ₽
                                    </span>
                                )}
                                <span className="total-price" style={{ fontSize: '20px', fontWeight: '700', color: '#ffffff' }}>
                                    {totalPrice} ₽
                                </span>
                            </div>
                        </div>
                        {/*{hasDiscount && (*/}
                        {/*    <div style={{ fontSize: '11px', color: '#ff9800', textAlign: 'right', marginTop: '4px' }}>*/}
                        {/*        Скидка {discountPercent}%*/}
                        {/*    </div>*/}
                        {/*)}*/}
                    </div>

                    <div className="renew-sheet-buttons" style={{ gap: '8px', paddingBottom: '25px'}}>
                        <button
                            onClick={handleClose}
                            style={{
                                flex: 1,
                                padding: '12px',
                                background: 'transparent',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '12px',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Отмена
                        </button>
                        <button
                            onClick={handleConfirm}
                            style={{
                                flex: 1,
                                padding: '12px',
                                background: 'linear-gradient(135deg, rgba(32, 213, 94, 0.38) 0%, #32D756 100%)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                color: 'white',
                                borderRadius: '12px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Продлить
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}